import type {TextEditorHighlights} from 'platform/components';

import {isNil} from 'ramda';

import {getWordDiff, isDiffInputTooLarge} from './getWordDiff';
import type {AiMessageComposerController} from './useAiMessageComposer';

type HighlightState = Pick<
  AiMessageComposerController,
  'isEnabled' | 'isChangeHighlighted' | 'canUndo' | 'undoStep' | 'isHtmlBody'
>;

/** The exact text-node string lets the editor reject stale or differently rendered decorations. */
const getRenderedText = (body: string, isHtml: boolean) =>
  isHtml ? (new DOMParser().parseFromString(body, 'text/html').body.textContent ?? '') : body;

export const getAiComposerEditorHighlights = (
  state: HighlightState
): TextEditorHighlights | undefined => {
  if (!state.isEnabled || !state.isChangeHighlighted || !state.canUndo || isNil(state.undoStep)) {
    return undefined;
  }

  const previous = getRenderedText(state.undoStep.previous.body, state.isHtmlBody);
  const current = getRenderedText(state.undoStep.applied.body, state.isHtmlBody);
  // A draft written into an empty field is all "change": marking every word tells the employee
  // nothing and stacks into blotches on wrapped lines. Only an edit of existing text is marked.
  if (previous.trim() === '' || isDiffInputTooLarge(previous, current)) {
    return undefined;
  }

  let index = 0;
  const ranges = getWordDiff(previous, current).flatMap((part) => {
    if (part.type === 'removed') {
      return [];
    }
    const start = index;
    index += part.value.length;
    return part.type === 'added' ? [{index: start, length: part.value.length}] : [];
  });

  return {text: current, ranges};
};
